<div class="stripe-onboarding-container">
  <!-- Stripe Balance GL Account: read-only, system-assigned, and first so loading/error blocks never shift it -->
  @if (stripeBalanceAccount(); as balanceAccount) {
    <div class="border rounded p-3 mb-3">
      <div class="text-muted small mb-1">Stripe payments are deposited to</div>
      <div class="fw-semibold">{{ balanceAccount.accountNumber }} - {{ balanceAccount.name }}</div>
      @if (balanceAccount.description) {
        <div class="text-muted small mt-1">{{ balanceAccount.description }}</div>
      }
    </div>
  }

  <!-- Loading State -->
  @if (isLoadingConnection()) {
    <div class="d-flex justify-content-center align-items-center p-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <span class="ms-3">Loading...</span>
    </div>
  }

  <!-- Error State -->
  @if (hasError() && !isLoadingConnection()) {
    <div class="alert alert-danger" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      <strong>Error:</strong> {{ errorMessage() }}
      <button type="button" class="btn-close float-end" (click)="clearError()"></button>
    </div>
  }

  <!-- Onboarding Complete State -->
  @if (isOnboardingComplete() && !isLoadingConnection()) {
    <div class="card">
      <div class="card-body">
        <div class="d-flex align-items-center">
          <i class="bi bi-check-circle-fill text-success fs-2"></i>
          <div>
            <h5 class="card-title mb-1">Stripe Onboarding Complete</h5>
            <p class="card-text text-muted mb-0">
              Your organization is successfully connected to Stripe and ready to accept payments.
            </p>
          </div>
        </div>
        @if (stripeConnection()?.stripeAccountId) {
          <div class="mt-3 pt-3 border-top">
            <small class="text-muted">
              <strong>Account ID:</strong> {{ stripeConnection()?.stripeAccountId }}
            </small>
          </div>
        }
      </div>
      <button
        [disableWithoutPermission]="unlinkPermission"
        type="button"
        class="btn btn-outline-danger m-3"
        [class.mb-2]="!canUnlinkStripe()"
        [attr.aria-describedby]="canUnlinkStripe() ? null : disconnectOwnerOnlyHelpTextId"
        (click)="openDisconnectConfirmationModal()">
        Disconnect from Stripe
      </button>
      @if (!canUnlinkStripe()) {
        <p class="text-muted small mx-3 mb-3" [id]="disconnectOwnerOnlyHelpTextId">{{ disconnectOwnerOnlyHelpText }}</p>
      }
    </div>
  }

  <!-- Onboarding Not Started State -->
  @if (canStartOnboarding() && !showEmbeddedOnboarding() && !isLoadingConnection() && !isPending()) {
    <h5 class="card-title">
      Connect with Stripe
    </h5>
    <p class="card-text">
      Connect your organization to Stripe to enable payment processing. You'll be guided through
      a secure onboarding process to set up your account.
    </p>
    <button
      [disableWithoutPermission]="createAccountPermission"
      type="button"
      class="btn btn-primary"
      [disabled]="isInitializing() || !canCreateStripeAccount()"
      [attr.aria-describedby]="canCreateStripeAccount() ? null : ownerOnlyHelpTextId"
      (click)="startOnboarding()">
      @if (isInitializing()) {
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
        Initializing...
      } @else {
        Start Stripe Onboarding
      }
    </button>
    @if (!canCreateStripeAccount()) {
      <p class="text-muted small mt-2 mb-0" [id]="ownerOnlyHelpTextId">{{ ownerOnlyHelpText }}</p>
    }
  }

  <!-- Embedded Onboarding Container -->
  @if (showEmbeddedOnboarding()) {
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="card-title mb-0">
        <i class="bi bi-stripe me-2"></i>
        Complete Stripe Onboarding
      </h5>
      <button
        [disableWithoutPermission]="createAccountPermission"
        type="button"
        class="btn btn-sm btn-outline-secondary"
        (click)="cancelOnboarding()">
        Cancel
      </button>
    </div>
    <div #onboardingContainer class="stripe-embedded-container"></div>
  }

  <!-- Pending State (Account exists but onboarding incomplete) -->
  @if (isPending() && !showEmbeddedOnboarding() && !isLoadingConnection()) {
        <div class="d-flex align-items-center">
          <i class="bi bi-exclamation-circle-fill text-warning fs-2 me-3"></i>
          <div>
            <h5 class="card-title mb-1">Stripe Onboarding Incomplete</h5>
            <p class="card-text text-muted mb-0">
              Your Stripe account setup is incomplete. Complete the onboarding process to begin accepting payments.
            </p>
            <button
              [disableWithoutPermission]="createAccountPermission"
              type="button"
              class="btn btn-warning mt-3"
              [disabled]="isInitializing() || !canCreateStripeAccount()"
              [attr.aria-describedby]="canCreateStripeAccount() ? null : ownerOnlyHelpTextId"
              (click)="startOnboarding()">
              @if (isInitializing()) {
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Initializing...
              } @else {
                Continue Onboarding
              }
            </button>
            @if (!canCreateStripeAccount()) {
              <p class="text-muted small mt-2 mb-0" [id]="ownerOnlyHelpTextId">{{ ownerOnlyHelpText }}</p>
            }
          </div>
        </div>
  }
</div>

<ms-confirmation-modal
  #stripeConfirmationModal
  modalId="stripeConfirmationModal"
  message="Are you sure you want to delete your Stripe connection?"
  confirmButtonText="Disconnect"
  cancelButtonText="Cancel"
  (confirmed)="disconnectStripe()"
  (cancelled)="cancelDisconnectStripe()"
  [isSubmitting]="isLoadingConnection()">
</ms-confirmation-modal>

<ms-info-modal
  #stripeInfoModal
  modalId="stripeInfoModal"
  (closed)="closeInfoModal()">
</ms-info-modal>